import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getDashboardActions, remindAction, remindAllActions } from '../api/dashboardApi'
import {
  isQueryReady,
  type ActionSelectionKeys,
  type DashboardQuery,
  type PriorityCriteria,
} from '../types'
import { DASHBOARD_REFRESH_MS, queryKeyParts } from './useActionDashboard'

/** Alt tablonun sabit sayfa boyu (077 §K4 — kullanıcı isteği: "10'ar 10'ar"). */
export const ACTIONS_PAGE_SIZE = 10

/**
 * Alt tablonun sunucu-durumu (077 §K4) — panonun kendi sorgusundan **ayrı**.
 *
 * <p>Ayrı olmasının sebebi maliyet değil davranış: dilim seçimi ve sayfa değişimi yalnız bu listeyi
 * yeniler; donut, kişi grafiği ve "Son Güncelleme" damgası oldukları gibi kalır. Aynı sorguya bağlansaydı
 * her tıklama tüm panoyu yükleme durumuna düşürürdü.
 *
 * <p>`i18n.language` anahtarın parçasıdır: satırın dile duyarlı alanları (ör. `bucket` adı) sunucuda
 * `Accept-Language`'e göre çözülür → dil değişince önbellekteki eski dildeki yanıt sunulmamalı.
 *
 * <p>`placeholderData` kullanılmaz (068 gerekçesi aynen geçerli): filtre/sayfa değiştiğinde eski satırları
 * yeni filtreye aitmiş gibi göstermek yanıltıcı olurdu. Yenileme <b>hatasında</b> ise TanStack Query son
 * başarılı veriyi ekranda tutar.
 */
export function useDashboardActions(
  query: DashboardQuery,
  selection: ActionSelectionKeys,
  page: number,
  sort: string | undefined,
  enabled = true,
) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: [
      'dashboard-actions',
      i18n.language,
      // Seçim ÇOKLU olduğu için anahtara tek bir dizge olarak girer. `parseSelection` listeyi kararlı bir
      // sırada verdiğinden aynı küme, hangi sırayla tıklandığından bağımsız olarak aynı anahtarı üretir —
      // diziyi olduğu gibi koymak, sıra ayrıştığında aynı veriyi iki kez çektirirdi.
      selection.join(',') || 'default',
      page,
      sort ?? 'default',
      ...queryKeyParts(query),
    ],
    queryFn: () => getDashboardActions(query, selection, page, ACTIONS_PAGE_SIZE, sort),
    enabled: enabled && isQueryReady(query),
    refetchOnWindowFocus: false,
    // Pano ile AYNI aralık (077 §K9): ayrışırsa donut ile tablo farklı anların verisini gösterir.
    refetchInterval: DASHBOARD_REFRESH_MS,
    // Periyodik yoklama merkezî hata hunisinden (019) MUAFTIR: kullanıcı isteğiyle değil, arka planda
    // 30 saniyede bir çalışır ve başarısızlığın kullanıcıya görünen yüzeyi zaten satır-içi hata bandıdır
    // (`home-error`). Susturulmasaydı bir kesintide pano açık kaldığı sürece HER 30 SANİYEDE bir hata
    // toast'ı düşerdi — üstelik bandın üstüne. Aynı gerekçe ve aynı desen: `useUnreadCount` (058).
    meta: { suppressGlobalError: true },
  })
}

/**
 * Hatırlatma gönderimi (tekli + toplu — 077 §K6).
 *
 * <p><b>Hiçbir sorgu invalidate edilMEZ:</b> hatırlatma aksiyonu değiştirmez, yalnız bildirim kuyruğuna
 * satır yazar → listenin, donut'un ya da sayıların yenilenmesi için bir sebep yoktur. Kullanıcının
 * <b>kendi</b> bildirim rozeti de etkilenmez (alıcı aksiyonun sorumlusudur, gönderen değil).
 *
 * <p>Sonucu çağıran yorumlar: `totalCount > actionCount` → üst sınıra takıldı (uyarı),
 * `notificationCount === 0` → hiç satır üretilmedi (şablon/alıcı yok).
 */
export function useActionReminder() {
  const single = useMutation({ mutationFn: (actionId: number) => remindAction(actionId) })
  const bulk = useMutation({
    mutationFn: (vars: {
      query: DashboardQuery
      selection: ActionSelectionKeys
      criteria: PriorityCriteria
    }) => remindAllActions(vars.query, vars.selection, vars.criteria),
  })
  return { single, bulk }
}

/**
 * Aksiyon oluşturulduğunda panoyu tazeler (077 §K7 — "Aksiyon Oluştur" Genel Müdür'de anasayfadadır).
 * Yeni aksiyon taban kümeye girer; donut, kişi grafiği ve alt tablo eskimiş sayılar göstermemelidir.
 */
export function useInvalidateDashboard() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['action-dashboard'] })
    qc.invalidateQueries({ queryKey: ['dashboard-actions'] })
  }
}
